// components/imports/NewImport.tsx — wizard steps 1–3 (Task O): what is being brought over, from where (Alto preset or a
// generic CSV), which company and cut-over date, and the file. Uploading reads the header and auto-maps the columns.
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Card, Button, Field, Input, Select, Segmented } from '../ui';
import { FormError } from '../forms/common';
import { importsApi, IMPORT_KINDS, KIND_LABEL, KIND_HINT, ORDER_NOTE, type ImportKind, type ImportSource } from '../../lib/imports';

const lastMonthEnd = () => { const d = new Date(); d.setUTCDate(0); return d.toISOString().slice(0, 10); };

export function NewImport({ onCancel }: { onCancel: () => void }) {
  const nav = useNavigate();
  const [kind, setKind] = useState<ImportKind>('UNITS');
  const [source, setSource] = useState<ImportSource>('ALTO');
  const [preset, setPreset] = useState<string>('');
  const [entityId, setEntityId] = useState('');
  const [asOf, setAsOf] = useState(lastMonthEnd());
  const [file, setFile] = useState<File | null>(null);
  const meta = useQuery({ queryKey: ['imports-meta'], queryFn: importsApi.meta });
  const companies = useQuery({ queryKey: ['import-companies'], queryFn: importsApi.companies });
  const presets = (meta.data?.kinds.find((k) => k.kind === kind)?.presets ?? []).filter((p) => p.source === source);
  const chosen = presets.find((p) => p.key === preset) ?? presets[0];
  const create = useMutation({
    mutationFn: async () => importsApi.create({ kind, source, preset: chosen?.key, fileName: file!.name, text: await file!.text(), entityId: entityId || null, asOf: asOf || null }),
    onSuccess: (job) => nav(`/app/imports/${job.id}`),
  });

  return (
    <Card title="New import" subtitle={ORDER_NOTE}>
      <div className="space-y-4">
        <div>
          <p className="mb-2 text-[12px] font-medium text-ink-500">1 · What are you bringing over?</p>
          <div className="grid gap-2 sm:grid-cols-3">
            {IMPORT_KINDS.map((k) => (
              <button key={k} type="button" onClick={() => { setKind(k); setPreset(''); }} aria-pressed={kind === k}
                className={`rounded-lg border px-3 py-2.5 text-left transition ${kind === k ? 'border-bronze-500 bg-bronze-50' : 'border-ink-200 hover:bg-ink-50'}`}>
                <span className="block text-[13px] font-semibold text-ink-900">{KIND_LABEL[k]}</span>
                <span className="mt-0.5 block text-[11px] leading-snug text-ink-500">{KIND_HINT[k]}</span>
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 [&>*]:min-w-0">
          <Field label="2 · From" hint={chosen?.note}>
            <div className="flex flex-wrap gap-2">
              <Segmented items={(['ALTO', 'GENERIC'] as ImportSource[]).map((s) => ({ key: s, label: s === 'ALTO' ? 'Alto' : 'Any CSV' }))} value={source} onChange={(s) => { setSource(s); setPreset(''); }} />
              {presets.length > 1 && <Select className="!w-auto min-w-0 flex-1" value={chosen?.key ?? ''} onChange={(e) => setPreset(e.target.value)} aria-label="Preset">
                {presets.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}</Select>}
            </div>
            {chosen?.toConfirm && <span className="mt-2 block rounded-lg bg-amber-50 px-2.5 py-1.5 text-[11.5px] text-amber-800">Preset to confirm against a real Alto export — check every column in the next step.</span>}
          </Field>
          <Field label="3 · The file (CSV)" hint="Saved with the import; the first row must be the column headings.">
            <Input type="file" className="min-w-0 max-w-full !px-2 text-[12px]" accept=".csv,text/csv,text/plain" onChange={(e) => setFile(e.target.files?.[0] ?? null)} />
          </Field>
          <Field label="Owning company" hint={kind === 'UNITS' || kind === 'PROPERTIES' ? 'New buildings go into this company.' : kind === 'OPENING_BALANCES' ? 'The company this trial balance belongs to.' : 'Optional — records go where their building is.'}>
            <Select value={entityId} onChange={(e) => setEntityId(e.target.value)}>
              <option value="">—</option>
              {(companies.data ?? []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </Select>
          </Field>
          <Field label="Cut-over date (as at)" hint="Opening postings — deposits held, arrears, unpaid bills, the trial balance — are dated this day. It must be in an open period.">
            <Input type="date" className="min-w-0 max-w-full" value={asOf} onChange={(e) => setAsOf(e.target.value)} />
          </Field>
        </div>
        <FormError error={create.error} />
        <div className="flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          <Button disabled={!file || create.isPending} onClick={() => create.mutate()}>{create.isPending ? 'Reading…' : 'Upload and map columns'}</Button>
        </div>
      </div>
    </Card>
  );
}
